import { useAppDispatch } from 'app/hooks';
import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import FormattedMessage from 'components/formatters/FormattedMessage';
import LoadingSpinner from 'components/icons/LoadingSpinner';
import {
  getUserIdFromToken,
  initializeAuth,
  logout,
} from 'features/auth/authSlice';
import { VERIFY_TOKEN_INTERVAL } from 'helpers/consts';
import { useAuthStatus } from 'hooks/useAuth';

type AuthProviderProps = {
  children: React.ReactNode;
};

/**
 * AuthProvider handles authentication initialization.
 * Uses the authSlice for clean authentication state management.
 * All other auth logic happens through explicit actions (login, logout, etc.).
 *
 * This component should be placed high in the component hierarchy but INSIDE
 * the ModalProvider to avoid remounting modals when auth state changes.
 */
export function AuthProvider({ children }: AuthProviderProps) {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const authStatus = useAuthStatus();

  useEffect(() => {
    // Initialize auth only once on mount - always dispatch, don't check authStatus
    // This ensures that even if component remounts, we validate the token
    dispatch(initializeAuth());
  }, [dispatch]);

  // Periodic session validation - check every 5 minutes
  useEffect(() => {
    if (authStatus !== 'authenticated') return;
    const checkSession = async () => {
      // Check if we can get a valid userId from the token
      const userId = getUserIdFromToken();
      if (!userId) {
        // Token is gone or invalid, logout and redirect
        await dispatch(logout());
        navigate('/');
      }
    };
    // Check session immediately, then every 2 minutes
    const timeoutId = setInterval(checkSession, VERIFY_TOKEN_INTERVAL);
    return () => clearInterval(timeoutId);
  }, [authStatus, dispatch, navigate]);

  // Show loading spinner during initial check
  if (authStatus === 'checking') {
    return (
      <LoadingSpinner active={true} onDark={true}>
        <FormattedMessage id="NAVIGATION.LOADER.APPLICATION" />
      </LoadingSpinner>
    );
  }

  return <>{children}</>;
}
